import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import { X, LayoutGrid, Clock, ListChecks, Heart, Eye, Map, Edit3, FileText, Star, Calendar } from 'lucide-react';
import { getInterestScore } from '../../../src/lib/interestScore';
import { getPlanningFeedAddedDate, isPlanningFeedAddedToday } from '../../../utils/addedToday';
import {
  countBucket,
  trackPlanningUpdatesEvent,
} from '../../../lib/planningUpdatesAnalytics';

const Sidebar = () => {
  const { 
    isSidebarOpen, 
    setIsSidebarOpen, 
    selectedFilter,
    setSelectedFilter,
    viewMode,
    handleViewModeChange,
    applications,
    favorites,
    referenceDate,
    feedSummary,
    usesRemoteSummary,
  } = usePlanningContext();

  const [applicationsWithReports, setApplicationsWithReports] = useState([]);

  const resolveAddedDate = (app) => {
    const value = getPlanningFeedAddedDate(app);
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const hasInterestDecision = (app) => typeof app?.interesting === 'boolean';
  const getBackendInterestScore = (app) => {
    const confidence = Number(app?.interest_confidence);
    const normalizedConfidence = Number.isFinite(confidence)
      ? Math.max(0, Math.min(1, confidence))
      : 0.75;
    const scaleBonus = Number.isFinite(Number(app?.scale_rating)) ? Number(app.scale_rating) : 0;
    return Math.round(normalizedConfidence * 100 + scaleBonus);
  };

  // Load reports from localStorage
  useEffect(() => {
    const loadReports = () => {
      try {
        const storedReports = JSON.parse(localStorage.getItem('planning_reports') || '[]');
        
        // Get unique applications that have reports
        const uniqueAppRefs = [...new Set(storedReports.map(r => r.reference_no))];
        const appsWithReports = applications.filter(app => 
          uniqueAppRefs.includes(app.reference_no)
        );
        setApplicationsWithReports(appsWithReports);
      } catch (error) {
        console.error('Failed to load reports:', error);
        setApplicationsWithReports([]);
      }
    };

    loadReports();

    // Listen for reports updates
    const handleReportsUpdated = () => {
      loadReports();
    };

    window.addEventListener('reportsUpdated', handleReportsUpdated);
    return () => {
      window.removeEventListener('reportsUpdated', handleReportsUpdated);
    };
  }, [applications]);

  // Featured Applications Logic - Added Today
  const todayData = useMemo(() => {
    const scoreApp = (app) => {
      if (app?.interesting === true) return { ...app, interestScore: getBackendInterestScore(app) };
      if (hasInterestDecision(app)) return null;
      const base = getInterestScore(app);
      const liberalScore = Math.round(base * 0.8 + (app.scale_rating ?? 0) * 3);
      return liberalScore >= 25 ? { ...app, interestScore: liberalScore } : null;
    };

    const todaysApps = applications.filter(app => isPlanningFeedAddedToday(app, referenceDate));

    const standout = todaysApps
      .map(scoreApp)
      .filter(Boolean)
      .sort((a, b) => b.interestScore - a.interestScore)
      .slice(0, 12);

    return { standout, allCount: todaysApps.length };
  }, [applications, referenceDate]);

  // Featured Applications Logic - This Week (same added-date basis as the hub)
  const weeklyData = useMemo(() => {
    const scoreApp = (app) => {
      if (app?.interesting === true) return { ...app, interestScore: getBackendInterestScore(app) };
      if (hasInterestDecision(app)) return null;
      const base = getInterestScore(app);
      const added = resolveAddedDate(app) ?? referenceDate;
      const days = Math.max(0, Math.floor((referenceDate.getTime() - added.getTime()) / (1000 * 60 * 60 * 24)));
      const recencyMultiplier = 1 + (0.4 / (1 + Math.exp(days - 3)));
      const scaleBonus = (app.scale_rating ?? 0) * 2;
      const composite = Math.round(base * recencyMultiplier + scaleBonus);
      return composite >= 50 ? { ...app, interestScore: composite } : null;
    };

    const isWithinLastWeek = (date) => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
      const now = referenceDate;
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return date >= weekAgo && date <= now;
    };

    const recentApps = applications.filter(app => {
      const added = resolveAddedDate(app);
      if (!added) return false;
      return isWithinLastWeek(added);
    });

    const standout = recentApps.map(scoreApp)
      .filter(Boolean)
      .sort((a, b) => b.interestScore - a.interestScore)
      .slice(0, 12);

    return { standout, allCount: recentApps.length };
  }, [applications, referenceDate]);

  const sidebarVariants = {
    closed: { x: "-100%", transition: { type: "tween", ease: "easeInOut", duration: 0.3 } },
    open: { x: "0%", transition: { type: "spring", stiffness: 300, damping: 30 } }
  };

  const getSidebarAnalyticsPlacement = () => {
    if (typeof window === 'undefined') return 'planning_sidebar_menu';
    return window.innerWidth >= 1280 ? 'desktop_sidebar_menu' : 'hamburger_sidebar_menu';
  };

  const menuItems = [
    {
      title: "Quick Views",
      items: [
        {
          label: "All Applications",
          filter: "all",
          icon: <ListChecks size={16} />,
          count: usesRemoteSummary ? feedSummary?.counts.total ?? null : applications.length,
        },
        { label: "Recent Applications", filter: "recent", icon: <Clock size={16} />, count: usesRemoteSummary ? feedSummary?.counts.recent ?? null : applications.filter(a => {
          const resolved = resolveAddedDate(a);
          if (!resolved) return false;
          const diff = (Date.now() - resolved.getTime()) / 86400000;
          return diff <= 7;
        }).length },
        { label: "Favorites", filter: "favorites", icon: <Heart size={16} />, count: favorites.length },
        {
          label: "Worth Watching",
          filter: "worth-watching",
          icon: <Eye size={16} />,
          count: usesRemoteSummary ? feedSummary?.counts.interesting ?? null : applications.filter(app => app?.interesting === true).length,
        },
      ]
    },
    {
      title: "Featured Applications",
      items: [
        {
          label: "Added Today",
          filter: "added-today",
          icon: <Calendar size={16} />,
          count: usesRemoteSummary ? feedSummary?.counts.today ?? null : todayData.allCount,
          description: "Applications added today",
          highlight: todayData.standout.length > 0
        },
        {
          label: "Weekly Highlights",
          filter: "weekly-highlights",
          icon: <Star size={16} />,
          count: usesRemoteSummary ? feedSummary?.counts.recent ?? null : weeklyData.allCount,
          description: "Applications added in the last 7 days"
        },
      ]
    },
    {
      title: "Reports",
      items: [
        { 
          label: "Generated Reports", 
          filter: "reports", 
          icon: <FileText size={16} />, 
          count: usesRemoteSummary ? null : applicationsWithReports.length,
          description: "Applications with AI-generated reports"
        },
      ]
    }
  ];

  const viewOptions = [
    { label: 'Grid', mode: 'grid', icon: <LayoutGrid size={14} /> },
    { label: 'Timeline', mode: 'timeline', icon: <Edit3 size={14} /> },
    { label: 'Paper', mode: 'newspaper', icon: <Eye size={14} /> },
    { label: 'Map', mode: 'map', icon: <Map size={14} /> },
  ];

  return (
    <AnimatePresence>
      {isSidebarOpen && (
        <motion.aside
          key="sidebar"
          className="pr-sidebar"
          style={{
            top: 'calc(var(--navbar-height, 64px) + var(--planning-region-rail-height, 0px))',
            height: 'calc(100vh - var(--navbar-height, 64px) - var(--planning-region-rail-height, 0px))',
          }}
          variants={sidebarVariants}
          initial="closed"
          animate="open"
          exit="closed"
        >
          <div className="pr-sidebar-inner">
            <header className="pr-sidebar-header">
              <div className="min-w-0">
                <p className="pr-kicker pr-kicker--accent">Navigation</p>
                <h2 className="pr-sidebar-title">Planning menu</h2>
              </div>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="pr-sidebar-close"
                aria-label="Close sidebar"
                type="button"
              >
                <X size={18} />
              </button>
            </header>
            
            <nav className="pr-sidebar-nav" aria-label="Planning menu">
              {menuItems.map((section) => (
                <section key={section.title} className="pr-sidebar-section">
                  <p className="pr-kicker">{section.title}</p>
                  <ul className="pr-sidebar-list">
                    {section.items.map((item, itemIndex) => {
                      const isActive = item.filter && selectedFilter === item.filter;
                      return (
                      <li key={`sidebar-${section.title || 'section'}-${itemIndex}`}>
                        <button
                          onClick={() => {
                            if (item.filter) {
                              trackPlanningUpdatesEvent('planning_updates_sidebar_filter_selected', {
                                placement: getSidebarAnalyticsPlacement(),
                                status: item.filter,
                                reason: selectedFilter,
                                value: typeof item.count === 'number' ? item.count : undefined,
                                metadata: {
                                  previous_filter: selectedFilter,
                                  next_filter: item.filter,
                                  item_count: typeof item.count === 'number' ? item.count : 0,
                                  item_count_bucket: countBucket(typeof item.count === 'number' ? item.count : 0),
                                  section: section.title
                                }
                              });
                              setSelectedFilter(item.filter);
                            }
                            if (item.mode) {
                              trackPlanningUpdatesEvent('planning_updates_sidebar_view_mode_selected', {
                                placement: getSidebarAnalyticsPlacement(),
                                status: item.mode,
                                reason: viewMode,
                                metadata: {
                                  previous_view_mode: viewMode,
                                  next_view_mode: item.mode,
                                  section: section.title
                                }
                              });
                              handleViewModeChange(item.mode);
                            }
                          }}
                          aria-current={isActive ? 'page' : undefined}
                          className="pr-sidebar-item"
                          type="button"
                        >
                          <span className="pr-sidebar-item-icon">
                            {React.cloneElement(item.icon, { size: 15, strokeWidth: 1.8 })}
                          </span>
                          <span className="pr-sidebar-item-copy">
                            <span className="pr-sidebar-item-topline">
                              <span className="pr-sidebar-item-label">{item.label}</span>
                              {item.count !== undefined && (
                                <span className="pr-sidebar-item-count" aria-label={item.count === null ? 'Count unavailable' : undefined}>
                                  {item.count?.toLocaleString('en-GB') ?? '—'}
                                </span>
                              )}
                            </span>
                            {item.description && (
                              <span className="pr-sidebar-item-description">
                                {item.description}
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );})}
                  </ul>
                </section>
              ))}

            </nav>

            <section className="pr-sidebar-view" aria-label="Planning view mode">
              <p className="pr-kicker">View mode</p>
              <div className="pr-sidebar-view-grid">
                {viewOptions.map((opt) => (
                  <button
                    key={opt.mode}
                    type="button"
                    aria-pressed={viewMode === opt.mode}
                    onClick={() => {
                      trackPlanningUpdatesEvent('planning_updates_sidebar_view_mode_selected', {
                        placement: getSidebarAnalyticsPlacement(),
                        status: opt.mode,
                        reason: viewMode,
                        metadata: {
                          previous_view_mode: viewMode,
                          next_view_mode: opt.mode,
                          section: 'View mode'
                        }
                      });
                      handleViewModeChange(opt.mode);
                    }}
                    className="pr-sidebar-view-button"
                  >
                    {React.cloneElement(opt.icon, { size: 15, strokeWidth: 1.8 })}
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </section>
            
            <footer className="pr-sidebar-footer">
              {(todayData.standout.length > 0 || weeklyData.standout.length > 0) && (
                <div className="pr-sidebar-metrics" aria-label="Planning menu summary">
                  <div className="pr-sidebar-metric">
                    <p className="pr-kicker">Today</p>
                    <p className="pr-sidebar-metric-value">{usesRemoteSummary ? feedSummary?.counts.today ?? '—' : todayData.allCount}</p>
                  </div>
                  <div className="pr-sidebar-metric">
                    <p className="pr-kicker">This week</p>
                    <p className="pr-sidebar-metric-value">{usesRemoteSummary ? feedSummary?.counts.recent ?? '—' : weeklyData.allCount}</p>
                  </div>
                </div>
              )}
              
              <div className="pr-sidebar-brand">
                <span className="pr-sidebar-brand-mark" aria-hidden="true">CS</span>
                <span>City Scope v1.2.0</span>
              </div>
            </footer>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;
